import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Users, Infinity, Clock, Loader2, Copy, Check, BarChart3 } from "lucide-react";

const AdminCodes = () => {
  const { user, access, loading } = useAuth();
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [newType, setNewType] = useState("FOREVER");
  const [newDays, setNewDays] = useState(30);
  const [newMax, setNewMax] = useState(1);
  const [newNotes, setNewNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/codes").catch(() => ({ data: { codes: [] } })),
      api.get("/admin/stats").catch(() => ({ data: null })),
    ]);
    setCodes(a.data.codes || []);
    setStats(b.data);
  };

  useEffect(() => {
    if (access?.is_admin) load();
  }, [access]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (access && !access.is_admin) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="admin-forbidden">
        <h1 className="font-serif text-3xl">Admin only.</h1>
        <p className="text-neutral-400 mt-3">You don't have access to this page.</p>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/codes", {
        code: newCode.trim().toUpperCase(),
        type: newType,
        days: newType === "TRIAL_EXTEND" ? Number(newDays) : 0,
        max_uses: Number(newMax),
        notes: newNotes,
      });
      toast.success("Code created.");
      setNewCode(""); setNewNotes(""); setNewMax(1); setNewDays(30);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create code");
    }
    setCreating(false);
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Deactivate code ${code}? It will stop working.`)) return;
    await api.post(`/admin/codes/${code}/deactivate`);
    toast("Code deactivated");
    load();
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1200);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="overline mb-2">ADMIN</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-6" data-testid="admin-title">
        Promo Codes <span className="italic text-amber-500">&amp; Clients</span>
      </h1>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: "Users", value: stats.total_users },
            { label: "In Trial", value: stats.in_trial },
            { label: "Paying", value: stats.active_subscriptions },
            { label: "Promo", value: stats.promo_users },
            { label: "Grandfathered", value: stats.grandfathered },
          ].map((s) => (
            <div key={s.label} className="card-surface p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="overline text-neutral-500">{s.label}</div>
              <div className="font-serif text-3xl mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-6">
        {/* Create code */}
        <form onSubmit={create} className="md:col-span-2 card-surface p-6 h-fit">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-amber-500"/> New code</h2>
          <label className="overline text-neutral-500">Code</label>
          <input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} required placeholder="COACHCLIENT" className="w-full mt-1 mb-4 px-3 py-2.5 bg-[#0a0a0b] border border-white/10 rounded-lg text-white tracking-wider" data-testid="admin-new-code"/>

          <label className="overline text-neutral-500">Type</label>
          <div className="flex gap-2 mt-1 mb-4">
            {[
              { id: "FOREVER", label: "Forever free" },
              { id: "TRIAL_EXTEND", label: "Extend days" },
            ].map((t) => (
              <button key={t.id} type="button" onClick={() => setNewType(t.id)} className={`flex-1 px-3 py-2 rounded-lg text-sm border ${newType === t.id ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400"}`} data-testid={`admin-type-${t.id}`}>{t.label}</button>
            ))}
          </div>

          {newType === "TRIAL_EXTEND" && (
            <>
              <label className="overline text-neutral-500">Days to grant</label>
              <input type="number" min={1} value={newDays} onChange={(e) => setNewDays(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2.5 bg-[#0a0a0b] border border-white/10 rounded-lg text-white" data-testid="admin-new-days"/>
            </>
          )}

          <label className="overline text-neutral-500">Max uses</label>
          <input type="number" min={1} value={newMax} onChange={(e) => setNewMax(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2.5 bg-[#0a0a0b] border border-white/10 rounded-lg text-white" data-testid="admin-new-max"/>

          <label className="overline text-neutral-500">Notes</label>
          <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="e.g. Maria — paid client Jan 2026" className="w-full mt-1 mb-5 px-3 py-2.5 bg-[#0a0a0b] border border-white/10 rounded-lg text-white" data-testid="admin-new-notes"/>

          <button type="submit" disabled={creating} className="btn-amber w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="admin-create-btn">
            {creating && <Loader2 className="w-4 h-4 animate-spin"/>} Create code
          </button>
        </form>

        {/* Codes list */}
        <div className="md:col-span-3 space-y-3" data-testid="admin-codes-list">
          {codes.length === 0 && <div className="card-surface p-6 text-neutral-500 text-sm">No codes yet. Create one on the left.</div>}
          {codes.map((c) => (
            <div key={c.code} className={`card-surface p-5 ${c.active ? "" : "opacity-50"}`} data-testid={`admin-code-${c.code}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-lg text-amber-400">{c.code}</span>
                    <button onClick={() => copy(c.code)} className="text-neutral-500 hover:text-white" title="Copy">
                      {copiedCode === c.code ? <Check className="w-3.5 h-3.5 text-amber-400"/> : <Copy className="w-3.5 h-3.5"/>}
                    </button>
                    {!c.active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30">Inactive</span>}
                  </div>
                  <div className="text-xs text-neutral-500 flex flex-wrap gap-3 items-center">
                    <span className="inline-flex items-center gap-1">
                      {c.type === "FOREVER" ? <Infinity className="w-3 h-3"/> : <Clock className="w-3 h-3"/>}
                      {c.type === "FOREVER" ? "Forever free" : `+${c.days} days`}
                    </span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3"/> {c.uses} / {c.max_uses} used</span>
                  </div>
                  {c.notes && <p className="text-sm text-neutral-400 mt-2 italic">{c.notes}</p>}
                </div>
                {c.active && (
                  <button onClick={() => deactivate(c.code)} className="text-neutral-500 hover:text-red-400 shrink-0" title="Deactivate" data-testid={`admin-deactivate-${c.code}`}>
                    <Trash2 className="w-4 h-4"/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {stats?.recent_redemptions?.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-amber-500"/> Recent redemptions</h2>
          <div className="space-y-2">
            {stats.recent_redemptions.map((r, i) => (
              <div key={i} className="card-surface p-3 flex items-center justify-between text-sm">
                <span className="font-mono text-amber-400">{r.code}</span>
                <span className="text-neutral-500">{new Date(r.redeemed_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCodes;
